import React, { useEffect, useState } from 'react';
import MANIFEST from './logoAnimations.json';

// Which logo a slot shows. An animated SVG plays once when it loads and can't decide anything
// itself, so the choice is made here: the still logo, or an animated one picked by a policy.
export const STATIC_LOGO = '/aveqlogo.svg';

// Built by tools/logo/build.py. aspect: image width / height. scale: how much taller the image
// must be drawn for its logo, at rest, to match the still one (room for the motion).
export const ANIMATIONS = MANIFEST;

// A policy gets the animations that fit and returns one of them, or null for the still logo.
export const randomPolicy = (chance) => ({ rng, animations }) =>
  (rng() < chance ? animations[Math.floor(rng() * animations.length)] : null);

export const DEFAULT_POLICY = randomPolicy(1 / 3);
export const EVERY_TIME = randomPolicy(1);

export function prefersReducedMotion() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const STILL = { src: STATIC_LOGO, scale: 1 };
let plays = 0;

// The logo for a slot `height` px tall with `width` px of room.
export function chooseLogo({ height, width = 0, reduceMotion = false,
                             policy = DEFAULT_POLICY, rng = Math.random }) {
  if (reduceMotion || prefersReducedMotion()) return STILL;
  const animations = ANIMATIONS.filter((a) => a.aspect * a.scale * height <= width);
  if (!animations.length) return STILL;
  const pick = policy({ rng, animations });
  if (!pick) return STILL;
  // Every copy of one SVG URL shares a single animation clock; a fresh URL starts it over.
  plays += 1;
  return { src: `${pick.src}?play=${plays}`, scale: pick.scale };
}

// Without `cue`, chosen once per mount, so the logo doesn't change under the reader. With it,
// still at first and chosen afresh each time the cue changes (for a logo that is always there).
export function BrandLogo({ height, width, reduceMotion, policy, cue }) {
  const [logo, setLogo] = useState(() => (
    cue === undefined ? chooseLogo({ height, width, reduceMotion, policy }) : STILL));
  useEffect(() => {
    if (cue) setLogo(chooseLogo({ height, width, reduceMotion, policy }));
  }, [cue]);  // eslint-disable-line react-hooks/exhaustive-deps
  // The extra height (room for the motion) overflows upward, so the slot's layout height stays
  // `height` and nothing below moves when an animation is chosen.
  return (
    <img src={logo.src} alt="Ave Q"
         style={{ height: `${height * logo.scale}px`, marginTop: `${-height * (logo.scale - 1)}px`,
                  display: 'block', flexShrink: 0 }} />
  );
}
